import React from 'react';
import { StandardButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';

/**
 * Select/Copy/Delete 3-dot menu + "Exit Select" button for a modal's item
 * list (checklist items, material list items, ...). Extracted from
 * ChecklistForm.tsx (2026-09-27, Material Lists point 3) so a second
 * consumer doesn't hand-roll a second copy of the same dropdown -- both
 * ChecklistForm and MaterialOrderForm import this now.
 *
 * `children` renders BETWEEN the "Exit Select" button and the dropdown
 * trigger, matching ChecklistForm's original visual order (Exit Select,
 * then "+ Add Item", then the 3-dot menu) without hardcoding an Add Item
 * button into this generic component -- each consumer's own item-add
 * affordance stays theirs.
 */
export const hoverAsCancel = {
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.backgroundColor = 'transparent'; },
};

interface ItemSelectionMenuProps {
  isSelectMode: boolean;
  hasSelection: boolean;
  onEnterSelectMode: () => void;
  onExitSelectMode: () => void;
  onCopy: () => void;
  onDelete: () => void;
  children?: React.ReactNode;
}

export function ItemSelectionMenu({
  isSelectMode,
  hasSelection,
  onEnterSelectMode,
  onExitSelectMode,
  onCopy,
  onDelete,
  children,
}: ItemSelectionMenuProps) {
  return (
    <>
      {isSelectMode && (
        <StandardButton type="button" customVariant="white" onClick={onExitSelectMode}>
          Exit Select
        </StandardButton>
      )}
      {children}
      {/* the app's one three-dot menu (StandardButtons): same size, icon, colours, hover, focus and item style as every other section */}
      <ThreeDotMenuButton
        items={[
          { label: 'Select', onClick: onEnterSelectMode, disabled: isSelectMode },
          { label: 'Copy', onClick: onCopy, disabled: !isSelectMode || !hasSelection },
          { label: 'Delete', onClick: onDelete, disabled: !isSelectMode || !hasSelection },
        ]}
      />
    </>
  );
}
